/**
 * Where this account is signed in — one row, both apps.
 *
 * Small enough to be tempting to copy and exactly the kind of thing that then
 * drifts: the office profile and Rowted Admin's would disagree about when the
 * button is disabled, or one would count the current session and the other
 * would not. `GET /me/sessions` answers the same shape on both sides
 * (`routes/platform-me.ts`), so the row is the same row.
 */
import { useCallback, useEffect, useState } from 'react'
import { describeAgent } from '@rowted/shared'
import { SettingRow } from '../components/settings-card.js'
import { Button } from '../components/primitives.js'
import { IconMonitor } from '../frame/icons.js'
import { formatNumber } from '../lib/format.js'
import { useAccountApi } from './account-api.js'

export interface DeviceRow {
  id: string
  surface: string
  ip: string | null
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
  current: boolean
}

export function useDevices(): {
  devices: DeviceRow[]
  /** Null until the read lands — the row says "looking…" rather than "0". */
  loaded: boolean
  reload: () => Promise<void>
} {
  const { request } = useAccountApi()
  const [devices, setDevices] = useState<DeviceRow[]>([])
  const [loaded, setLoaded] = useState(false)

  const reload = useCallback(async () => {
    try {
      const body = await request<{ sessions: DeviceRow[] }>('/me/sessions')
      setDevices(Array.isArray(body?.sessions) ? body.sessions : [])
    } catch {
      setDevices([])
    } finally {
      setLoaded(true)
    }
  }, [request])

  useEffect(() => { void reload() }, [reload])
  return { devices, loaded, reload }
}

export function SignedInDevicesRow({ devices, loaded, busy, onSignOutOthers }: {
  devices: DeviceRow[]
  loaded: boolean
  busy: boolean
  onSignOutOthers: () => void
}) {
  return (
    <SettingRow
      icon={<IconMonitor size={17} />}
      label="Signed-in devices"
      sub={!loaded
        ? 'looking…'
        : devices.length === 0
          ? 'none open'
          : devices.map((one) => describeAgent(one.userAgent)
            + (one.current ? ' (this one)' : '')).join(' · ')}
      value={`${formatNumber(devices.length)} active`}
      action={(
        <Button
          className="h-[30px]"
          /* Nothing to sign out of but the one you are using. */
          disabled={busy || devices.length < 2}
          onClick={onSignOutOthers}
        >
          Sign out others
        </Button>
      )}
    />
  )
}
